import { CutlistOrder } from '@cutr/constants/cutlist';
import { QueryClient } from '@tanstack/react-query';
import { LoaderFunctionArgs, redirect } from 'react-router-dom';

import { useLeadDetails } from '@/api/account';
import { useDeliveryAddress } from '@/api/address';
import { isValidBeamPart, isValidPart } from '@/api/store';
import { partMapper } from '@/hooks';
import { fetchMaterials } from '@/queries/loaders';

import { hydrateStarterCutlist } from './hydrate';
import { getStarterCutlistQuery } from './queries';

const hasValidPart = (cutlist: CutlistOrder) =>
  cutlist.materialGroups
    .flatMap((g) => g.parts)
    .map(partMapper)
    .some((part) =>
      part.partType === 'beam' ? isValidBeamPart(part) : isValidPart(part)
    );

export const canEnterDetails = (cutlist: CutlistOrder) =>
  hasValidPart(cutlist) &&
  cutlist.materialGroups
    .filter((g) => !g.beamMaterialId)
    .every((g) => Boolean(g.nestingOutput));

export const canEnterReview = (cutlist: CutlistOrder) => {
  if (!canEnterDetails(cutlist)) return false;

  const leadName =
    cutlist.userLead?.name ||
    cutlist.userLeadDetail?.name ||
    useLeadDetails.getState().name;
  const email = cutlist.userLead?.email || useLeadDetails.getState().email;
  const address = cutlist.addresses[0];
  const liveAddress = useDeliveryAddress.getState();
  const line1 = address?.line1 || liveAddress.line1;
  const city = address?.city || liveAddress.city;

  return Boolean(leadName && email && line1 && city);
};

const DRAFT_STEP = /\/(parts|details|review)\/?$/;

export const starterCutlistLoader =
  (queryClient: QueryClient) =>
  async ({ params, request }: LoaderFunctionArgs) => {
    const id = params.id as string;

    await fetchMaterials();

    const url = new URL(request.url);

    const cutlist = await queryClient.ensureQueryData(
      getStarterCutlistQuery(id)
    );

    await hydrateStarterCutlist(
      cutlist,
      url.searchParams.get('material') ?? undefined
    );

    if (cutlist.submittedAt && DRAFT_STEP.test(url.pathname)) {
      return redirect(`/cutlist/${id}`);
    }

    return cutlist;
  };

export const starterIndexLoader =
  (queryClient: QueryClient) =>
  async ({ params }: LoaderFunctionArgs) => {
    const id = params.id as string;
    const cutlist = await queryClient.ensureQueryData(
      getStarterCutlistQuery(id)
    );

    if (!cutlist.submittedAt) return redirect(`/cutlist/${id}/parts`);
    return null;
  };

export const starterDetailsLoader =
  (queryClient: QueryClient) =>
  async ({ params }: LoaderFunctionArgs) => {
    const id = params.id as string;
    const cutlist = await queryClient.ensureQueryData(
      getStarterCutlistQuery(id)
    );

    if (!canEnterDetails(cutlist)) return redirect(`/cutlist/${id}/parts`);
    return null;
  };

export const starterReviewLoader =
  (queryClient: QueryClient) =>
  async ({ params }: LoaderFunctionArgs) => {
    const id = params.id as string;
    const cutlist = await queryClient.ensureQueryData(
      getStarterCutlistQuery(id)
    );

    if (!canEnterReview(cutlist)) return redirect(`/cutlist/${id}/details`);
    return null;
  };
